import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import type { PermissionAccessCatalogItem } from '../api/strapiPermissionPresets';
import {
  usePermissionAccessCatalogQuery,
  useSaveUserPermissionPresetMutation,
  useUserDetailQuery,
} from '../query/hooks';

export type UserPermissionPresetEditorHandle = {
  save: () => Promise<void>;
};

type Props = {
  jwt: string | null;
  userId: string | null | undefined;
  readOnly?: boolean;
};

function groupCatalog(items: PermissionAccessCatalogItem[]) {
  const map = new Map<
    string,
    { label: string; sortKey: string; items: PermissionAccessCatalogItem[] }
  >();
  for (const item of items) {
    const g = item.group;
    const label = g?.group_name?.trim() || 'Other';
    const sortKey = g?.group_name?.trim() || '\uffff';
    const key =
      g?.documentId ?? (g?.numericId != null ? `id:${g.numericId}` : `ungrouped:${label}:${item.access_code}`);
    const cur = map.get(key) ?? { label, sortKey, items: [] };
    cur.items.push(item);
    map.set(key, cur);
  }
  return [...map.values()].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
}

export const UserPermissionPresetEditor = forwardRef<UserPermissionPresetEditorHandle | null, Props>(
  function UserPermissionPresetEditor({ jwt, userId, readOnly = false }, ref) {
    const uid = userId && Number.isFinite(Number(userId)) ? userId : null;
    const { data: user, isPending: userPending } = useUserDetailQuery(jwt, uid);
    const { data: catalog, isPending: catalogPending } = usePermissionAccessCatalogQuery(
      jwt,
      Boolean(uid),
    );
    const saveMutation = useSaveUserPermissionPresetMutation(jwt);

    const [selected, setSelected] = useState<Set<string>>(new Set());

    useEffect(() => {
      if (!user) return;
      const ids = user.accesses.map((a) => a.documentId).filter(Boolean) as string[];
      setSelected(new Set(ids));
    }, [user]);

    const grouped = useMemo(() => groupCatalog(catalog ?? []), [catalog]);

    useImperativeHandle(
      ref,
      () => ({
        save: async () => {
          if (!uid || !jwt) return;
          await saveMutation.mutateAsync({
            userId: uid,
            permissionAccessDocumentIds: [...selected],
          });
        },
      }),
      [uid, jwt, selected, saveMutation],
    );

    function toggle(documentId: string) {
      if (readOnly || !documentId) return;
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(documentId)) next.delete(documentId);
        else next.add(documentId);
        return next;
      });
    }

    const pending = userPending || catalogPending;

    return (
      <Stack spacing={2}>
        {pending && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading permissions…</Typography>
          </Stack>
        )}
        {!pending && !catalog?.length && (
          <Typography variant="body2" color="text.secondary">
            No permission accesses defined in Strapi.
          </Typography>
        )}
        {!pending &&
          grouped.map((g, idx) => (
            <Stack key={`${g.sortKey}:${g.label}:${idx}`} spacing={1}>
              {idx > 0 ? <Divider sx={{ my: 1 }} /> : null}
              <Typography variant="subtitle2">{g.label}</Typography>
              <FormGroup sx={{ pl: 0.5 }}>
                {g.items.map((item) => {
                  const docId = item.documentId ?? '';
                  if (!docId) {
                    return (
                      <Typography
                        key={item.numericId ?? item.access_code}
                        variant="body2"
                        color="error"
                      >
                        Missing documentId for {item.access_code}
                      </Typography>
                    );
                  }
                  return (
                    <FormControlLabel
                      key={docId}
                      disabled={readOnly}
                      control={
                        <Checkbox
                          size="small"
                          checked={selected.has(docId)}
                          onChange={() => toggle(docId)}
                        />
                      }
                      label={
                        <>
                          <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                            {item.access_code}
                          </Typography>{' '}
                          <Typography component="span" variant="body2" color="text.secondary">
                            — {item.access_name}
                          </Typography>
                        </>
                      }
                    />
                  );
                })}
              </FormGroup>
            </Stack>
          ))}
        {saveMutation.isError && (
          <Typography variant="body2" color="error">
            {saveMutation.error instanceof Error
              ? saveMutation.error.message
              : 'Unable to save permission preset.'}
          </Typography>
        )}
      </Stack>
    );
  },
);
